export default function LeagueTable({ stats = [], highlight = true }) {
  const formatPct = (val) => (isFinite(val) ? `${Math.round(val * 100)}%` : "--");
  const safeMax = (vals) => (vals.length ? Math.max(...vals) : 0);

  const maxValues = highlight
    ? {
        regular_wins: safeMax(stats.map((r) => r.regular_wins ?? 0)),
        regular_losses: safeMax(stats.map((r) => r.regular_losses ?? 0)),
        postseason_appearances: safeMax(stats.map((r) => r.postseason_appearances ?? 0)),
        post_wins: safeMax(stats.map((r) => r.post_wins ?? 0)),
        post_losses: safeMax(stats.map((r) => r.post_losses ?? 0)),
        championships: safeMax(stats.map((r) => r.championships ?? 0)),
        regular_win_pct: safeMax(stats.map((r) => r.regular_win_pct ?? 0)),
        post_win_pct: safeMax(stats.map((r) => r.post_win_pct ?? 0)),
      }
    : {};

  const isHL = (cond) => highlight && cond;

  const numCls = (cond) =>
    isHL(cond)
      ? "theme-display text-[var(--highlight-text)]"
      : "tabular-nums text-[var(--table-text)]";

  const cellStyle = (cond) =>
    isHL(cond)
      ? { background: "var(--highlight-bg)", boxShadow: "inset 0 0 0 1px rgba(242, 223, 180, 0.18)" }
      : {};

  const groupBorderL = { borderLeft: "1px solid var(--border-strong)" };

  const MobileLeagueTable = () => (
    <div className="overflow-hidden rounded-xl border sm:hidden" style={{ borderColor: "var(--border-soft)" }}>
      <div className="grid grid-cols-[minmax(0,0.25fr)_minmax(0,1.7fr)_minmax(0,0.8fr)_minmax(0,0.9fr)_minmax(0,0.65fr)_minmax(0,0.95fr)_minmax(1.25rem,0.4fr)_minmax(0,0.06fr)] items-end border-b text-[7px] font-semibold uppercase leading-tight tracking-[0.1em]" style={{ borderColor: "var(--border-soft)", background: "var(--table-head-bg)", color: "var(--table-head-text)" }}>
        <span aria-hidden="true" />
        <span className="flex min-h-[2.75rem] items-center justify-center px-1.5 py-2 text-center text-[8px]">Manager</span>
        <span className="flex min-h-[2.75rem] items-center justify-center border-l px-1 py-2 text-center text-[8px]" style={{ borderColor: "var(--border-soft)" }}>Seasons</span>
        <span className="border-l px-1 py-2 text-center" style={{ borderColor: "var(--border-soft)" }}>Regular<br />Season<br /><span className="font-normal tracking-normal opacity-80">W-L / %</span></span>
        <span className="flex min-h-[2.75rem] items-center justify-center border-l px-1 py-2 text-center" style={{ borderColor: "var(--border-soft)" }}>Playoff<br />Berths</span>
        <span className="border-l px-1 py-2 text-center" style={{ borderColor: "var(--border-soft)" }}>Post-<br />Season<br /><span className="font-normal tracking-normal opacity-80">W-L / %</span></span>
        <span aria-label="Titles" className="flex min-h-[2.75rem] w-full items-center justify-center border-l px-1 py-2 text-center text-sm leading-none" style={{ borderColor: "var(--border-soft)" }}>🏆</span>
        <span aria-hidden="true" />
      </div>

      <div>
        {stats.map((row, idx) => {
          const stripe = idx % 2 !== 0 ? { background: "var(--table-stripe-bg, rgba(255,255,255,0.03))" } : { background: "var(--table-body-bg)" };
          const berthsIsMax = (row.postseason_appearances ?? 0) === maxValues.postseason_appearances;
          const titlesIsMax = (row.championships ?? 0) === maxValues.championships;

          return (
            <div key={idx} className="grid grid-cols-[minmax(0,0.25fr)_minmax(0,1.7fr)_minmax(0,0.8fr)_minmax(0,0.9fr)_minmax(0,0.65fr)_minmax(0,0.95fr)_minmax(1.25rem,0.4fr)_minmax(0,0.06fr)] items-center border-b last:border-b-0" style={{ borderColor: "var(--border-soft)", ...stripe }}>
              <span aria-hidden="true" />
              <div className="flex min-w-0 items-center px-1.5 py-2">
                <span className="min-w-0 truncate whitespace-nowrap text-[9px] font-bold leading-tight" style={{ color: "var(--text-main)" }} title={row.name}>{row.name}</span>
              </div>
              <span className="border-l px-1 py-2 text-center text-[11px] font-semibold tabular-nums" style={{ borderColor: "var(--border-soft)", color: "var(--table-text)" }}>{row.active_seasons ?? 0}</span>
              <span className="border-l px-1 py-1.5 text-center text-[9px] font-semibold leading-tight tabular-nums" style={{ borderColor: "var(--border-soft)", color: "var(--table-text)" }}>
                <span className="block tabular-nums">{row.regular_wins ?? 0}-{row.regular_losses ?? 0}</span>
                <span className="block text-[8px] tabular-nums opacity-80">{formatPct(row.regular_win_pct)}</span>
              </span>
              <span className={`border-l px-1 py-1.5 text-center text-[10px] font-semibold tabular-nums ${numCls(berthsIsMax)}`} style={{ borderColor: "var(--border-soft)", ...(berthsIsMax ? cellStyle(true) : {}) }}>{row.postseason_appearances ?? 0}</span>
              <span className="border-l px-1 py-1.5 text-center text-[8px] font-semibold leading-tight" style={{ borderColor: "var(--border-soft)", color: "var(--table-text)" }}>
                <span className="block text-[9px] tabular-nums">{row.post_wins ?? 0}-{row.post_losses ?? 0}</span>
                <span className="block text-[8px] tabular-nums" style={{ color: "var(--muted-text)" }}>{formatPct(row.post_win_pct)}</span>
              </span>
              <span className={`flex w-full items-center justify-center border-l px-1 py-1.5 text-center text-[11px] font-semibold ${numCls(titlesIsMax)}`} style={{ borderColor: "var(--border-soft)", ...(titlesIsMax ? cellStyle(true) : {}) }}>{row.championships ?? 0}</span>
              <span aria-hidden="true" />
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="w-full">
      <MobileLeagueTable />
      <table className="hidden table-fixed border-separate border-spacing-0 w-full min-w-[332px] sm:table sm:min-w-[900px] text-[9px] sm:text-sm">
        <colgroup>
          <col style={{ width: "120px" }} />
          <col style={{ width: "28px" }} />
          {/* Regular Season */}
          <col style={{ width: "28px" }} />
          <col style={{ width: "28px" }} />
          <col style={{ width: "32px" }} />
          {/* Postseason */}
          <col style={{ width: "28px" }} />
          <col style={{ width: "28px" }} />
          <col style={{ width: "28px" }} />
          <col style={{ width: "32px" }} />
          {/* Titles */}
          <col style={{ width: "28px" }} />
        </colgroup>

        <thead style={{ background: "var(--table-head-bg)", color: "var(--table-head-text)" }}>
          {/* Column group row */}
          <tr className="uppercase tracking-widest text-[7px] sm:text-[9px] lg:text-[10px]">
            <th className="px-1 pb-0 pt-2 text-left sm:px-4" style={{ color: "var(--muted-text)" }} />
            <th className="px-0.5 pb-0 pt-2 text-center sm:px-4" style={{ color: "var(--muted-text)" }} />
            <th
              colSpan={3}
              className="pb-0 pt-2 text-center font-semibold tracking-[0.14em] sm:pt-3 lg:tracking-[0.2em]"
              style={{ color: "var(--accent-soft)", borderLeft: "1px solid var(--border-soft)" }}
            >
              <span className="lg:hidden">Regular Season</span>
              <span className="hidden lg:inline">Regular Season</span>
            </th>
            <th
              colSpan={4}
              className="pb-0 pt-2 text-center font-semibold tracking-[0.14em] sm:pt-3 lg:tracking-[0.2em]"
              style={{ color: "var(--accent-soft)", borderLeft: "1px solid var(--border-soft)" }}
            >
              Postseason
            </th>
            <th
              className="pb-0 pt-2 text-center sm:pt-3"
              style={{ borderLeft: "1px solid var(--border-soft)" }}
            />
          </tr>

          {/* Column label row */}
          <tr className="uppercase tracking-tight sm:tracking-[0.04em] lg:tracking-wider">
            <th className="sticky left-0 z-20 border-b px-1 pb-2 pt-1 text-left text-[8px] leading-none sm:px-4 sm:pb-4 sm:pt-2 sm:text-xs" style={{ borderColor: "var(--border-soft)", background: "var(--table-sticky-bg)" }}>
              Manager
            </th>
            <th className="border-b px-0.5 pb-2 pt-1 text-center text-[8px] leading-none sm:px-4 sm:pb-4 sm:pt-2 sm:text-xs" style={{ borderColor: "var(--border-soft)" }}>
              <span className="lg:hidden">Szns</span>
              <span className="hidden lg:inline">Seasons</span>
            </th>
            <th className="border-b px-0.5 pb-2 pt-1 text-center text-[8px] leading-none sm:px-4 sm:pb-4 sm:pt-2 sm:text-xs" style={{ borderColor: "var(--border-soft)", ...groupBorderL }}>
              <span className="lg:hidden">W</span>
              <span className="hidden lg:inline">Wins</span>
            </th>
            <th className="border-b px-0.5 pb-2 pt-1 text-center text-[8px] leading-none sm:px-4 sm:pb-4 sm:pt-2 sm:text-xs" style={{ borderColor: "var(--border-soft)" }}>
              <span className="lg:hidden">L</span>
              <span className="hidden lg:inline">Losses</span>
            </th>
            <th className="border-b px-0.5 pb-2 pt-1 text-center text-[8px] leading-none sm:px-4 sm:pb-4 sm:pt-2 sm:text-xs" style={{ borderColor: "var(--border-soft)" }}>
              <span className="lg:hidden">W%</span>
              <span className="hidden lg:inline">Win %</span>
            </th>
            <th className="border-b px-0.5 pb-2 pt-1 text-center text-[8px] leading-none sm:px-4 sm:pb-4 sm:pt-2 sm:text-xs" style={{ borderColor: "var(--border-soft)", ...groupBorderL }}>
              <span className="lg:hidden">Berths</span>
              <span className="hidden text-[10px] leading-tight lg:inline-block">Playoff Berths</span>
            </th>
            <th className="border-b px-0.5 pb-2 pt-1 text-center text-[8px] leading-none sm:px-4 sm:pb-4 sm:pt-2 sm:text-xs" style={{ borderColor: "var(--border-soft)" }}>
              <span className="lg:hidden">W</span>
              <span className="hidden lg:inline">Wins</span>
            </th>
            <th className="border-b px-0.5 pb-2 pt-1 text-center text-[8px] leading-none sm:px-4 sm:pb-4 sm:pt-2 sm:text-xs" style={{ borderColor: "var(--border-soft)" }}>
              <span className="lg:hidden">L</span>
              <span className="hidden lg:inline">Losses</span>
            </th>
            <th className="border-b px-0.5 pb-2 pt-1 text-center text-[8px] leading-none sm:px-4 sm:pb-4 sm:pt-2 sm:text-xs" style={{ borderColor: "var(--border-soft)" }}>
              <span className="lg:hidden">W%</span>
              <span className="hidden lg:inline">Win %</span>
            </th>
            <th className="border-b px-0.5 pb-2 pt-1 text-center text-[8px] leading-none sm:px-4 sm:pb-4 sm:pt-2 sm:text-xs" style={{ borderColor: "var(--border-soft)", ...groupBorderL }}>
              <span className="lg:hidden" aria-label="Titles">🏆</span>
              <span className="hidden lg:inline">Titles 🏆</span>
            </th>
          </tr>
        </thead>

        <tbody>
          {stats.map((row, idx) => {
            const stripe = idx % 2 !== 0 ? { background: "var(--table-stripe-bg, rgba(255,255,255,0.03))" } : { background: "var(--table-body-bg)" };

            return (
              <tr key={idx} className="transition-all duration-150 hover:brightness-[1.22] hover:contrast-[1.05]">
                <td
                  className="sticky left-0 z-10 border-b px-1 py-2.5 text-left text-[9px] font-medium leading-tight sm:px-4 sm:py-3.5 sm:text-sm"
                  style={{ borderColor: "var(--border-soft)", background: "var(--table-sticky-bg)", color: "var(--text-main)" }}
                  title={row.name}
                >
                  <span className="block truncate">{row.name}</span>
                </td>

                <td className="border-b px-0.5 py-2.5 text-center text-[9px] tabular-nums sm:px-4 sm:py-3.5 sm:text-sm" style={{ borderColor: "var(--border-soft)", color: "var(--table-text)", ...stripe }}>
                  {row.active_seasons ?? 0}
                </td>

                {/* Regular Season */}
                <td className={`border-b px-0.5 py-2.5 text-center text-[9px] sm:px-4 sm:py-3.5 sm:text-sm ${numCls(row.regular_wins === maxValues.regular_wins)}`}
                  style={{ borderColor: "var(--border-soft)", ...groupBorderL, ...cellStyle(row.regular_wins === maxValues.regular_wins), ...(!(row.regular_wins === maxValues.regular_wins && highlight) ? stripe : {}) }}>
                  {row.regular_wins ?? 0}
                </td>
                <td className={`border-b px-0.5 py-2.5 text-center text-[9px] sm:px-4 sm:py-3.5 sm:text-sm ${numCls(row.regular_losses === maxValues.regular_losses)}`}
                  style={{ borderColor: "var(--border-soft)", ...cellStyle(row.regular_losses === maxValues.regular_losses), ...(!(row.regular_losses === maxValues.regular_losses && highlight) ? stripe : {}) }}>
                  {row.regular_losses ?? 0}
                </td>
                <td className={`border-b px-0.5 py-2.5 text-center text-[9px] sm:px-4 sm:py-3.5 sm:text-sm ${numCls((row.regular_win_pct ?? 0) === maxValues.regular_win_pct)}`}
                  style={{ borderColor: "var(--border-soft)", ...cellStyle((row.regular_win_pct ?? 0) === maxValues.regular_win_pct), ...(!(((row.regular_win_pct ?? 0) === maxValues.regular_win_pct) && highlight) ? stripe : {}) }}>
                  {formatPct(row.regular_win_pct)}
                </td>

                {/* Postseason */}
                <td className={`border-b px-0.5 py-2.5 text-center text-[9px] sm:px-4 sm:py-3.5 sm:text-sm ${numCls((row.postseason_appearances ?? 0) === maxValues.postseason_appearances)}`}
                  style={{ borderColor: "var(--border-soft)", ...groupBorderL, ...cellStyle((row.postseason_appearances ?? 0) === maxValues.postseason_appearances), ...(!((row.postseason_appearances ?? 0) === maxValues.postseason_appearances && highlight) ? stripe : {}) }}>
                  {row.postseason_appearances ?? 0}
                </td>
                <td className={`border-b px-0.5 py-2.5 text-center text-[9px] sm:px-4 sm:py-3.5 sm:text-sm ${numCls((row.post_wins ?? 0) === maxValues.post_wins)}`}
                  style={{ borderColor: "var(--border-soft)", ...cellStyle((row.post_wins ?? 0) === maxValues.post_wins), ...(!((row.post_wins ?? 0) === maxValues.post_wins && highlight) ? stripe : {}) }}>
                  {row.post_wins ?? 0}
                </td>
                <td className={`border-b px-0.5 py-2.5 text-center text-[9px] sm:px-4 sm:py-3.5 sm:text-sm ${numCls((row.post_losses ?? 0) === maxValues.post_losses)}`}
                  style={{ borderColor: "var(--border-soft)", ...cellStyle((row.post_losses ?? 0) === maxValues.post_losses), ...(!((row.post_losses ?? 0) === maxValues.post_losses && highlight) ? stripe : {}) }}>
                  {row.post_losses ?? 0}
                </td>
                <td className={`border-b px-0.5 py-2.5 text-center text-[9px] sm:px-4 sm:py-3.5 sm:text-sm ${numCls((row.post_win_pct ?? 0) === maxValues.post_win_pct)}`}
                  style={{ borderColor: "var(--border-soft)", ...cellStyle((row.post_win_pct ?? 0) === maxValues.post_win_pct), ...(!((row.post_win_pct ?? 0) === maxValues.post_win_pct && highlight) ? stripe : {}) }}>
                  {formatPct(row.post_win_pct)}
                </td>

                {/* Titles */}
                <td className={`border-b px-0.5 py-2.5 text-center text-[9px] sm:px-4 sm:py-3.5 sm:text-sm ${numCls((row.championships ?? 0) === maxValues.championships)}`}
                  style={{ borderColor: "var(--border-soft)", ...groupBorderL, ...cellStyle((row.championships ?? 0) === maxValues.championships), ...(!((row.championships ?? 0) === maxValues.championships && highlight) ? stripe : {}) }}>
                  {row.championships > 0 ? (
                    <>
                      <span className="lg:hidden">{row.championships}</span>
                      <span className="hidden lg:inline">
                        {`${row.championships} (${(row.championship_years || []).map((y) => `'${String(y).slice(-2)}`).join(", ")})`}
                      </span>
                    </>
                  ) : (
                    <span className="tabular-nums text-[var(--table-text)] opacity-30">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
